import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const services = [
  { icon: "ri-bank-card-line", colorClass: "text-cyan-500", bgClass: "bg-cyan-50", borderHover: "hover:border-cyan-400/40", titleKey: "top.service1.title", descKey: "top.service1.desc", num: "01" },
  { icon: "ri-exchange-line", colorClass: "text-emerald-500", bgClass: "bg-emerald-50", borderHover: "hover:border-emerald-400/40", titleKey: "top.service2.title", descKey: "top.service2.desc", num: "02" },
  { icon: "ri-money-dollar-circle-line", colorClass: "text-violet-400", bgClass: "bg-violet-900/20", borderHover: "hover:border-violet-400/40", titleKey: "top.service3.title", descKey: "top.service3.desc", num: "03" },
  { icon: "ri-bar-chart-grouped-line", colorClass: "text-amber-400", bgClass: "bg-amber-900/20", borderHover: "hover:border-amber-400/40", titleKey: "top.service4.title", descKey: "top.service4.desc", num: "04" },
  { icon: "ri-robot-line", colorClass: "text-rose-400", bgClass: "bg-rose-900/20", borderHover: "hover:border-rose-400/40", titleKey: "top.service5.title", descKey: "top.service5.desc", num: "05" },
  { icon: "ri-dashboard-3-line", colorClass: "text-sky-400", bgClass: "bg-sky-900/20", borderHover: "hover:border-sky-400/40", titleKey: "top.service6.title", descKey: "top.service6.desc", num: "06" },
];

const ServicesSection = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative bg-slate-950 overflow-hidden">
      {/* Diagonal top from white */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-white"
        style={{ clipPath: "polygon(0 0, 0 100%, 100% 0)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 lg:pb-32">
        {/* Header: left-aligned with big number */}
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div
            className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="w-1 h-12 bg-gradient-to-b from-cyan-500 to-transparent mb-4" />
            <span className="text-cyan-400 text-xs font-bold tracking-[0.3em] uppercase block mb-2">{t("top.services.label")}</span>
            <h2 className="text-4xl lg:text-6xl font-black text-white leading-none">{t("top.services.title")}</h2>
          </div>
          <div
            className={`text-right transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <p className="text-slate-400 text-sm max-w-xs leading-relaxed">{t("top.services.sub")}</p>
          </div>
        </div>

        {/* Services: mixed layout - 2 wide + 4 narrow */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* First 2: wide cards with image bg */}
          {services.slice(0, 2).map((svc, i) => (
            <div
              key={svc.titleKey}
              className={`lg:col-span-6 group relative overflow-hidden rounded-2xl border border-slate-800 ${svc.borderHover} transition-all duration-500 cursor-pointer min-h-[200px] ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${0.1 + i * 0.1}s` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 group-hover:from-slate-800 transition-all duration-500" />
              {/* Big number watermark */}
              <div className="absolute top-4 right-6 text-7xl font-black text-white/5 select-none group-hover:text-white/8 transition-all duration-500">
                {svc.num}
              </div>
              <div className="relative z-10 p-8">
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${svc.bgClass} mb-6`}>
                  <i className={`${svc.icon} ${svc.colorClass} text-2xl`} />
                </div>
                <h3 className={`text-white font-bold text-xl mb-3 group-hover:${svc.colorClass} transition-colors duration-300`}>
                  {t(svc.titleKey)}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">{t(svc.descKey)}</p>
                <div className={`mt-6 flex items-center gap-2 ${svc.colorClass} text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line" />
                </div>
              </div>
            </div>
          ))}

          {/* Next 4: compact cards */}
          {services.slice(2).map((svc, i) => (
            <div
              key={svc.titleKey}
              className={`lg:col-span-3 group relative overflow-hidden rounded-2xl border border-slate-800 ${svc.borderHover} transition-all duration-500 cursor-pointer ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${0.3 + i * 0.08}s` }}
            >
              <div className="absolute inset-0 bg-slate-900/60 group-hover:bg-slate-800/80 transition-all duration-500" />
              <div className="absolute top-3 right-4 text-5xl font-black text-white/5 select-none">{svc.num}</div>
              <div className="relative z-10 p-6">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${svc.bgClass} mb-4`}>
                  <i className={`${svc.icon} ${svc.colorClass} text-lg`} />
                </div>
                <h3 className="text-white font-semibold text-sm mb-2 group-hover:text-cyan-300 transition-colors duration-300">
                  {t(svc.titleKey)}
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed">{t(svc.descKey)}</p>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`mt-12 transition-all duration-700 delay-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        >
          <Link
            to="/service"
            className="inline-flex items-center gap-2 px-8 py-4 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-white font-bold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            {t("nav.service")}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
